import React, { useState } from 'react';
import { AppItem, AppLanguage, PortfolioText } from '../types';
import { getScreenshotSrc } from '../utils/screenshots';
import { X, Smartphone, Globe, Code, Image as ImageIcon } from 'lucide-react';

interface AppDetailsModalProps {
  app: AppItem;
  language: AppLanguage;
  text: PortfolioText;
  onClose: () => void;
}

const formatText = (template: string, values: Record<string, string | number>) => (
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  )
);

const AppStoreIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="w-3.5 h-3.5 fill-current">
    <path d="M17.2 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.5-2.6-1.7-3.1-1.7-1.3-.1-2.6.8-3.3.8-.7 0-1.8-.8-2.9-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.8-.4 6.8 1.1 9.1.8 1.1 1.7 2.4 2.9 2.3 1.1 0 1.6-.7 2.9-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.2-2.6-.1 0-2.7-1-2.7-3.9Z" />
    <path d="M15 6.5c.6-.8 1.1-1.8 1-2.9-1 .1-2.1.7-2.8 1.4-.6.7-1.1 1.7-1 2.8 1.1.1 2.1-.5 2.8-1.3Z" />
  </svg>
);

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="w-3.5 h-3.5">
    <path fill="#34A853" d="M4.2 3.4c-.2.3-.3.7-.3 1.2v14.8c0 .5.1.9.3 1.2l8.7-8.6-8.7-8.6Z" />
    <path fill="#4285F4" d="m15.8 9.1-10-5.7c-.6-.3-1.1-.3-1.5 0l8.7 8.6 2.8-2.9Z" />
    <path fill="#FBBC04" d="m13 12-8.8 8.6c.4.3.9.3 1.5 0l10-5.7L13 12Z" />
    <path fill="#EA4335" d="m19.2 11-3.4-1.9L13 12l2.8 2.9 3.4-1.9c1.1-.6 1.1-1.4 0-2Z" />
  </svg>
);

export default function AppDetailsModal({ app, language, text, onClose }: AppDetailsModalProps) {
  const screenshots = app.screenshots || [];
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState<number | null>(null);
  const selectedScreenshot = selectedScreenshotIndex === null
    ? null
    : screenshots[selectedScreenshotIndex];
  const storeLinks = [
    {
      key: 'appstore',
      href: app.links.appstore,
      label: text.appstore_link_label,
      icon: <AppStoreIcon />,
    },
    {
      key: 'playstore',
      href: app.links.playstore,
      label: text.playstore_link_label,
      icon: <PlayStoreIcon />,
    },
  ].filter(link => link.href);
  const primaryType = app.types[0] ?? '';

  const getIcon = () => {
    if (app.icon) {
      return <img src={app.icon} alt="" className="w-7 h-7 object-contain" />;
    }

    const typeStr = primaryType.toLowerCase();
    if (typeStr.includes('native') || typeStr.includes('ios') || typeStr.includes('android')) {
      return <Smartphone size={28} className="text-indigo-600" />;
    }
    if (typeStr.includes('web') || typeStr.includes('react')) {
      return <Code size={28} className="text-emerald-600" />;
    }
    return <Globe size={28} className="text-sky-600" />;
  };

  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex items-center justify-center p-4">
      <div 
        className={`bg-white dark:bg-slate-900 rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col relative animate-in fade-in zoom-in-95 duration-200 font-sans text-slate-800 dark:text-slate-100 transition-colors duration-300 ${
          language.isRtl ? 'text-right' : 'text-left'
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={language.isRtl ? 'rtl' : 'ltr'}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className={`absolute top-4 p-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-full transition-all cursor-pointer z-10 ${language.isRtl ? 'left-4' : 'right-4'}`}
        >
          <X size={16} />
        </button>

        {/* Header Block */}
        <div className={`p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850/50 rounded-t-3xl transition-colors duration-300 ${
          language.isRtl ? 'pl-16' : 'pr-16'
        }`}>
          <div className="flex items-center gap-3 justify-start">
            {getIcon()}
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">{app.name}</h2>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6">
          {/* Main Description */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{text.modal_about_title}</h4>
            <p
              dir={language.isRtl ? 'rtl' : 'ltr'}
              className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap [unicode-bidi:plaintext]"
            >
              {app.description}
            </p>
          </div>

          {/* Details Config Grid */}
          <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-2xl border border-slate-200/50 dark:border-slate-800 transition-colors duration-300">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2.5">{text.modal_specs_title}</h4>
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 dark:text-slate-500">{text.modal_architecture_label}</span>
                <div className="flex flex-wrap gap-1.5">
                  {app.types.map(type => (
                    <span key={type} dir="ltr" className="bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-full px-2 py-0.5 text-[10px] font-extrabold">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              {app.techs && app.techs.length > 0 && (
                <div className="flex items-start gap-1.5 col-span-2">
                  <span className="text-slate-400 dark:text-slate-500 shrink-0 font-sans">{text.modal_technologies_label}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {app.techs.map(tech => (
                      <span key={tech} dir="ltr" className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-full px-2 py-0.5 text-[10px] font-bold">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Screenshots Gallery Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-start">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {formatText(text.modal_screenshots_title, { count: screenshots.length })}
              </h4>
            </div>

            {screenshots.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-x-auto pb-1.5">
                {screenshots.map((screen, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedScreenshotIndex(index)}
                    className="relative group/screen bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden aspect-video flex items-center justify-center cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
                  >
                    <img
                      src={getScreenshotSrc(screen)}
                      className="w-full h-full object-contain"
                      alt={formatText(text.screenshot_alt, { number: index + 1 })}
                      referrerPolicy="no-referrer"
                    />
                    
                    <div className="absolute bottom-2 right-2 bg-slate-900/60 dark:bg-slate-950/70 text-white text-[9px] font-bold px-2 py-0.5 rounded-md">
                      #{index + 1}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center text-slate-400 dark:text-slate-500 space-y-1">
                <ImageIcon size={22} className="mx-auto text-slate-300 dark:text-slate-600" />
                <p className="text-xs font-medium">{text.modal_no_screenshots}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        {storeLinks.length > 0 && (
          <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-850/30 rounded-b-3xl flex gap-3 items-center justify-start">
            {storeLinks.map(link => (
              <a 
                key={link.key}
                href={link.href} 
                target="_blank" 
                rel="noreferrer" 
                className="px-4 py-2 text-xs font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center gap-1.5 transition-colors"
              >
                {link.icon}
                <span>{link.label}</span>
              </a>
            ))}
          </div>
        )}
      </div>

      {selectedScreenshot && selectedScreenshotIndex !== null && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedScreenshotIndex(null)}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedScreenshotIndex(null);
            }}
            className={`absolute top-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer ${
              language.isRtl ? 'left-4' : 'right-4'
            }`}
          >
            <X size={18} />
          </button>
          <img
            src={getScreenshotSrc(selectedScreenshot)}
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            alt={formatText(text.screenshot_alt, { number: selectedScreenshotIndex + 1 })}
            referrerPolicy="no-referrer"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
